import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ActivityDuration, ToBeDone } from '../../../../../../db/types';
import { i18nNS } from '../../../../../../i18n';
import ChevronRightIcon from '../../../../../../icons/ChevronRightIcon';
import { format } from '../../../../../../utils/datetime';
import DurationDialog from '../DurationDialog';
import { Cell, CellButton, CellLabel, CellValue } from '../styles';
import Skeleton from './Skeleton';
import useDurationCellVM, { Props as VMProps } from './vm';

interface Props<T extends FieldValues> extends VMProps<T> {
  onUpdateDuration: (activityDuration: ActivityDuration) => any;
  className?: string;
}

const DurationCell = <T extends FieldValues>({
  onUpdateDuration: handleUpdateDuration,
  className,
  ...vmOptions
}: Props<T>) => {
  const { t } = useTranslation([i18nNS.POLL, i18nNS.GLOSSARY]);

  const {
    poll,
    durations,
    controllerMethods,
    isDurationDialogOpen,
    onCellClick: handleCellClick,
    onCloseDurationDialog: handleCloseDurationDialog,
  } = useDurationCellVM(vmOptions);

  const {
    field: { value: _value },
  } = controllerMethods;

  /**
   * We check this because if the selected value is not in the durations array,
   * we assign the default value ActivityDuration.BY_END_OF_THE_CLASS
   */
  const isValidOption =
    durations.includes(_value) || ActivityDuration.MANUALLY_STOPPED || ActivityDuration.BY_END_OF_THE_CLASS;
  const value = isValidOption ? _value : ActivityDuration.BY_END_OF_THE_CLASS;

  const options = [
    { label: t('manually', { ns: i18nNS.GLOSSARY }), value: ActivityDuration.MANUALLY_STOPPED },
    ...durations.map((duration) => {
      if (duration < 1) {
        return {
          label: t('N_seconds_after_publishing', { ns: i18nNS.POLL, count: duration * 60 }),
          value: duration,
        };
      }

      return {
        label: t('N_minutes_after_publishing', { ns: i18nNS.POLL, count: duration }),
        value: duration,
      };
    }),
    { label: t('when_class_ends', { ns: i18nNS.POLL }), value: ActivityDuration.BY_END_OF_THE_CLASS },
  ];

  if (!poll) return <Skeleton />;

  if (poll.toBeDone === ToBeDone.PRE_CLASS) {
    return (
      <Cell>
        <CellLabel>{t('deadline', { ns: i18nNS.GLOSSARY })}</CellLabel>
        <CellValue>{format(poll.dueDateTime, 'hh:mm a, MMM dd, yyyy')}</CellValue>
      </Cell>
    );
  }

  return (
    <>
      <CellButton
        variant="text"
        endIcon={<ChevronRightIcon aria-hidden />}
        onClick={handleCellClick}
        aria-label={t('click_to_choose_closes_on', { ns: i18nNS.POLL })}
        fullWidth
      >
        <Cell width="100%">
          <CellLabel variant="h6Bold">{t('poll_closes', { ns: i18nNS.POLL })}</CellLabel>
          <CellValue variant="h6Regular">{options.find((option) => option.value === value)?.label}</CellValue>
        </Cell>
      </CellButton>
      <DurationDialog
        open={isDurationDialogOpen}
        onClose={handleCloseDurationDialog}
        options={options}
        value={value}
        onUpdateDuration={handleUpdateDuration}
      />
    </>
  );
};

export default DurationCell;
